/* ------------------------------------------------------------------
  File: SectionHeader.jsx
  Author: Engr. Shaffa K. Kokro Jr. - CEO, Accend Holdings Ltd.
  Last Modified: 2026-05-18
------------------------------------------------------------------ */

// Purpose:
//   - Provides a reusable section heading component for the Accend Holdings website.
//   - Supports eyebrow text, title, supporting text, optional action, alignment,
//     width control, and dark-surface compatibility.
//   - Uses the acc-section-header namespace to avoid conflicts with Bootstrap.
//   - Used across page sections to maintain consistent heading structure.

/**
 * SectionHeader
 *
 * A reusable section heading component for page sections.
 *
 * @param {string} eyebrow
 * Optional small uppercase label displayed above the title.
 *
 * @param {string} title
 * Main section title.
 *
 * @param {string} text
 * Optional supporting text below the title.
 *
 * @param {React.ReactNode} action
 * Optional action element such as a Button or link.
 *
 * @param {"left" | "center"} [align="left"]
 * Controls text alignment.
 *
 * @param {"default" | "narrow" | "wide" | "full"} [width="default"]
 * Controls the maximum width of the header content.
 *
 * @param {string} [titleAs="h2"]
 * Controls the HTML heading element used for the title.
 *
 * @param {string} [className=""]
 * Additional custom class names.
 */

import "./SectionHeader.css";

const SectionHeader = ({
  eyebrow,
  title,
  text,
  action,
  align = "left",
  width = "default",
  titleAs: TitleTag = "h2",
  className = "",
  ...props
}) => {
  const sectionHeaderClasses = [
    "acc-section-header",
    `acc-section-header--${align}`,
    `acc-section-header--${width}`,
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={sectionHeaderClasses} {...props}>
      <div className="acc-section-header__content l-stack l-stack--sm">
        {eyebrow && (
          <span className="acc-section-header__eyebrow">{eyebrow}</span>
        )}

        {title && (
          <TitleTag className="acc-section-header__title">{title}</TitleTag>
        )}

        {text && <p className="acc-section-header__text">{text}</p>}
      </div>

      {action && <div className="acc-section-header__action">{action}</div>}
    </div>
  );
};

export default SectionHeader;